/**
 * Meta Pixel helper — iter47t.
 *
 * Mirrors the existing googleAds.js pattern. The Pixel ID is fetched
 * from GET /api/site/meta-pixel (which admin sets via the
 * MetaPixelSettingsTab) — NOT from an env var — so the operator can
 * swap Pixels at any time with no deploy.
 *
 * fbq() is the global Meta Pixel function; once MetaPixelLoader injects
 * the base code, every helper below just calls `fbq('track', ...)`.
 */

/**
 * Fire a standard Meta Pixel event. If fbq isn't loaded yet (e.g.
 * because the admin hasn't configured a Pixel ID, or the user denied
 * consent), this silently no-ops — never throws.
 *
 * Standard event names recognised by Meta:
 *   ViewContent, AddToCart, InitiateCheckout, Purchase, Lead,
 *   AddToWishlist, CompleteRegistration, Contact, FindLocation, Search.
 *
 * The optional 3rd `options` arg lets callers pass a deterministic
 * `eventID` — required for CAPI deduplication (iter47u). Without it,
 * the client-side and server-side Purchase events would double-count.
 */
export function trackMetaEvent(name, params = {}, options = {}) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    if (options && options.eventID) {
      window.fbq("track", name, params, { eventID: options.eventID });
    } else {
      window.fbq("track", name, params);
    }
  } catch {
    /* swallow — Meta sometimes throws if consent is in flight */
  }
}

// Convenience shortcuts — one per the 4 e-commerce events the user asked
// for. content_ids/contents wiring matches Meta's Commerce best-practices
// so dynamic ads can stitch click→view→purchase paths to specific SKUs.

const CAPI_RELAY = "PageView ViewContent AddToCart InitiateCheckout Lead";

/** Generate a stable event id shared by the browser Pixel + server CAPI. */
export function genEventId(name) {
  const rand = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${name}_${rand}`;
}

/**
 * Relay a browser event to the server-side Conversions API with the SAME
 * event_id so Meta dedupes the pair. Fire-and-forget — never blocks UI,
 * never throws. The access token lives ONLY server-side; this just posts
 * the event name + id + optional commerce/contact fields.
 */
export function relayCapi(name, eventId, { value, currency = "CAD", contents, custom_data, user_data } = {}) {
  if (typeof fetch === "undefined" || !CAPI_RELAY.includes(name) || !eventId) return;
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return;
  const { fbp, fbc, user_agent } = readMetaCookies();
  try {
    fetch(`${base}/api/capi/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event_name: name,
        event_id: eventId,
        value,
        currency,
        contents,
        custom_data,
        event_source_url: typeof window !== "undefined" ? window.location.href : undefined,
        user_data: { fbp, fbc, user_agent, ...(user_data || {}) },
      }),
    }).catch(() => {});
  } catch {
    /* ignore — analytics must never break the page */
  }
}

export function trackViewContent(product, { value, currency = "CAD" } = {}) {
  if (!product) return;
  const eventId = genEventId("ViewContent");
  const v = typeof value === "number" ? Number(value.toFixed(2)) : undefined;
  trackMetaEvent("ViewContent", {
    content_ids: [product.id],
    content_name: product.name,
    content_type: "product",
    content_category: product.category,
    value: v,
    currency,
  }, { eventID: eventId });
  relayCapi("ViewContent", eventId, {
    value: v, currency,
    contents: [{ id: product.id, quantity: 1, item_price: v }],
    custom_data: { content_type: "product", content_name: product.name },
  });
}

export function trackAddToCart({ id, name, value, quantity = 1, currency = "CAD" }) {
  const eventId = genEventId("AddToCart");
  const v = Number((value * quantity).toFixed(2));
  trackMetaEvent("AddToCart", {
    content_ids: [id],
    content_name: name,
    content_type: "product",
    contents: [{ id, quantity, item_price: value }],
    value: v,
    currency,
  }, { eventID: eventId });
  relayCapi("AddToCart", eventId, {
    value: v, currency,
    contents: [{ id, quantity, item_price: value }],
    custom_data: { content_type: "product", content_name: name },
  });
}

export function trackInitiateCheckout({ items = [], value = 0, currency = "CAD" }) {
  const eventId = genEventId("InitiateCheckout");
  const contents = items.map((it) => ({
    id: it.product_id || it.id,
    quantity: it.quantity || 1,
    item_price: it.unit_price || 0,
  }));
  const v = Number((value || 0).toFixed(2));
  trackMetaEvent("InitiateCheckout", {
    content_ids: items.map((it) => it.product_id || it.id).filter(Boolean),
    contents,
    num_items: items.reduce((acc, it) => acc + (it.quantity || 1), 0),
    value: v,
    currency,
  }, { eventID: eventId });
  relayCapi("InitiateCheckout", eventId, { value: v, currency, contents });
}

/**
 * Lead event — fire on the /quote form submit. `contact` may include
 * email/phone/first_name/last_name for enhanced server-side matching.
 */
export function trackLead({ value = 0, currency = "CAD", contact = {} } = {}) {
  const eventId = genEventId("Lead");
  trackMetaEvent("Lead", { value: Number((value || 0).toFixed(2)), currency }, { eventID: eventId });
  relayCapi("Lead", eventId, {
    value, currency,
    user_data: {
      email: contact.email,
      phone: contact.phone,
      first_name: contact.first_name,
      last_name: contact.last_name,
    },
  });
}

/** PageView — relayed server-side with a shared event_id for dedup. */
export function trackPageView() {
  const eventId = genEventId("PageView");
  trackMetaEvent("PageView", {}, { eventID: eventId });
  relayCapi("PageView", eventId, {});
}

export function trackPurchase({ orderNumber, items = [], value = 0, currency = "CAD" }) {
  trackMetaEvent("Purchase", {
    content_ids: items.map((it) => it.product_id || it.id).filter(Boolean),
    contents: items.map((it) => ({
      id: it.product_id || it.id,
      quantity: it.quantity || 1,
      item_price: it.unit_price || 0,
    })),
    num_items: items.reduce((acc, it) => acc + (it.quantity || 1), 0),
    value: Number((value || 0).toFixed(2)),
    currency,
    order_id: orderNumber,
  }, {
    // iter47u: stable event_id so server-side CAPI (fired from checkout.py)
    // dedupes with this client-side event. Format must match the backend:
    //   `order_${order_number}`.
    eventID: orderNumber ? `order_${orderNumber}` : undefined,
  });
}

/**
 * Read the `_fbp` and `_fbc` cookies that Meta drops on every browser
 * that's clicked a Meta ad or visited an Pixel-enabled page. Sending
 * them with the CAPI Purchase event roughly doubles attribution match
 * quality.
 */
export function readMetaCookies() {
  if (typeof document === "undefined") return {};
  const parse = (name) => {
    // Look for `name=...` in the cookie string. We don't bother escaping
    // because both `_fbp` and `_fbc` are static literals — no regex
    // metachars to worry about.
    const cookies = document.cookie.split("; ");
    for (const c of cookies) {
      const eq = c.indexOf("=");
      if (eq > 0 && c.slice(0, eq) === name) {
        return decodeURIComponent(c.slice(eq + 1));
      }
    }
    return null;
  };
  return {
    fbp: parse("_fbp") || undefined,
    fbc: parse("_fbc") || undefined,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  };
}
